import { Input, InputProps } from '@apia/theme';
import { usePrevious } from '@apia/util';
import { MutableRefObject, forwardRef, useCallback, useRef } from 'react';
import { getLabel } from '../../../../utils/util';
import useMultipleRefs from '../../../../hooks/useMultipleRefs';

export type TNumberInputProps = {
  value?: string;
  onChange?: (ev: { newValue?: string; error?: string }) => unknown;
} & Omit<InputProps, 'onChange'>;

export const NumberApiaInputPrimitive = forwardRef<
  HTMLInputElement,
  TNumberInputProps
>(({ onChange, value, ...props }, externalRef) => {
  const ref = useRef<HTMLInputElement>({} as HTMLInputElement);
  const assignRef = useMultipleRefs(
    ref,
    externalRef as unknown as MutableRefObject<HTMLInputElement>,
  );

  const previousValue = usePrevious(value);

  const parseIncomingValue = useCallback((newValue: string) => {
    const objNumRegExp = window.OBJ_NUM_REGEXP;
    let formattedValue = newValue;
    if (newValue === '') {
      formattedValue = '';
    } else if (objNumRegExp.test(newValue)) {
      // eslint-disable-next-line prefer-const
      let [integerPart, decimalPart] = newValue.split(
        window.CHAR_DECIMAL_SEPARATOR,
      );
      decimalPart = (decimalPart ?? '').slice(
        0,
        Number.parseInt(window.AMOUNT_DECIMAL_SEPARATOR),
      );
      for (
        let i = decimalPart.length;
        i < Number.parseInt(window.AMOUNT_DECIMAL_ZEROS);
        i++
      ) {
        decimalPart = `${decimalPart}0`;
      }
      formattedValue = `${integerPart}${
        decimalPart.length > 0
          ? `${window.CHAR_DECIMAL_SEPARATOR}${decimalPart}`
          : ''
      }`;
    } else {
      formattedValue = '';
    }
    ref.current.value = formattedValue;
  }, []);

  const parseWrittenValue = useCallback(
    (newValue: string) => {
      let [integerPart, decimalPart] = newValue.split(
        window.CHAR_DECIMAL_SEPARATOR,
      );
      decimalPart = decimalPart ?? '';
      integerPart = integerPart ?? '';
      integerPart = integerPart.replaceAll(window.CHAR_THOUS_SEPARATOR, '');

      if (integerPart === '' && decimalPart === '') {
        ref.current.value = '';
        onChange?.({ newValue: '' });
      } else if (!integerPart.match(/^-?\d+$/) || !decimalPart.match(/^\d*$/)) {
        ref.current.value = '';
        onChange?.({ newValue: '', error: getLabel('msgMustBeNum').text });
      } else {
        decimalPart = decimalPart.slice(
          0,
          Number.parseInt(window.AMOUNT_DECIMAL_SEPARATOR),
        );

        for (
          let i = decimalPart.length;
          i < Number.parseInt(window.AMOUNT_DECIMAL_ZEROS);
          i++
        ) {
          decimalPart = `${decimalPart}0`;
        }

        if (window.ADD_THOUSAND_SEPARATOR) {
          const integerWithoutSeparators = integerPart;
          const length = integerWithoutSeparators.length - 1;
          integerPart = '';
          for (let i = 0; i <= length; i++) {
            const addSeparator = i > 0 && i % 3 === 0;
            integerPart = `${integerWithoutSeparators.charAt(length - i)}${
              addSeparator ? window.CHAR_THOUS_SEPARATOR : ''
            }${integerPart}`;
          }
        }

        const value = `${integerPart}${
          decimalPart.length > 0
            ? `${window.CHAR_DECIMAL_SEPARATOR}${decimalPart}`
            : ''
        }`;

        ref.current.value = value;
        onChange?.({ newValue: value });
      }
    },
    [onChange],
  );

  if (previousValue.current !== value && ref.current instanceof HTMLElement) {
    parseIncomingValue(value ?? '');
  }

  return (
    <Input
      {...props}
      defaultValue={value}
      ref={assignRef}
      onKeyDown={(ev) => {
        if (ev.code === 'Enter') {
          parseWrittenValue((ev.target as HTMLInputElement).value);
        }
      }}
      onBlur={(ev) => {
        parseWrittenValue(ev.target.value);
      }}
      className={`numberInput ${props.className ?? ''}`}
    />
  );
});
